import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FadeIn } from '../ui/FadeIn';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiLink, FiCpu, FiLayers, FiDownload, FiUpload, FiEdit3, FiMic, FiYoutube, FiClock, FiWand2, FiStar } = FiIcons;

const pipelineData = [
  {
    id: 'yt-to-slides',
    tab: 'YouTube → Slides',
    shortTab: 'YT → Slides',
    heading: 'YouTube URL in. PowerPoint + video out.',
    steps: [
      { icon: FiLink, iconColor: 'text-brand-electric', bgColor: 'bg-brand-electric/20', title: 'Paste any YouTube URL', body: 'Any public video. VidRevive downloads, transcribes, and analyzes the full content in seconds.' },
      { icon: FiCpu, iconColor: 'text-brand-neon', bgColor: 'bg-brand-neon/20', title: 'AI structures the content', body: 'Key concepts identified, learning objectives created, content organized into a logical slide flow — not a raw transcript dump.' },
      { icon: FiLayers, iconColor: 'text-brand-mint', bgColor: 'bg-brand-mint/20', title: 'Branded slides + rebuilt narration', body: 'Slides match your brand kit. Narration is rewritten for clarity — not the original rambling video audio.' },
      { icon: FiDownload, iconColor: 'text-brand-electric', bgColor: 'bg-brand-electric/20', title: 'Download PPTX, PDF, and video', body: 'Export the slide deck in any format. Get the narrated video too. Optional: animated motion slides powered by AI.' }
    ]
  },
  {
    id: 'slides-to-yt',
    tab: 'Slides → YouTube',
    shortTab: 'Slides → YT',
    heading: 'Upload a deck. Get a YouTube-ready video.',
    steps: [
      { icon: FiUpload, iconColor: 'text-brand-electric', bgColor: 'bg-brand-electric/20', title: 'Upload PPTX, PDF, or Google Slides', body: 'Any presentation format. VidRevive reads every slide — text, structure, and images.' },
      { icon: FiEdit3, iconColor: 'text-brand-neon', bgColor: 'bg-brand-neon/20', title: 'AI writes video narration', body: 'Each slide gets a video-ready script — not a text readout, but engaging narration built for watching, not reading.' },
      { icon: FiMic, iconColor: 'text-brand-mint', bgColor: 'bg-brand-mint/20', title: 'Your voice synthesized', body: 'ElevenLabs synthesizes narration in your cloned voice (or choose from 5 built-in voices). Sounds natural on every slide.' },
      { icon: FiYoutube, iconColor: 'text-brand-electric', bgColor: 'bg-brand-electric/20', title: 'Download or publish directly', body: 'Branded MP4 with transitions, captions, and music. Includes AI-generated thumbnail + title/description for YouTube.' }
    ]
  },
  {
    id: 'old-to-new',
    tab: 'Old Video → New Video',
    shortTab: 'Rebrand',
    heading: 'Same knowledge. New video. New brand.',
    steps: [
      { icon: FiClock, iconColor: 'text-brand-electric', bgColor: 'bg-brand-electric/20', title: 'Paste your old YouTube video', body: 'Your 2018 lecture, your dated Zoom recording, your old explainer. VidRevive analyzes what made it valuable.' },
      { icon: FiWand2, iconColor: 'text-brand-neon', bgColor: 'bg-brand-neon/20', title: 'AI rewrites for today', body: 'Script restructured for current engagement patterns. New slides replace old talking-head footage. Brand kit applied.' },
      { icon: FiMic, iconColor: 'text-brand-mint', bgColor: 'bg-brand-mint/20', title: 'Fresh narration, your voice', body: 'New AI narration replaces the original audio. Motion video effects bring the new slides to life.' },
      { icon: FiStar, iconColor: 'text-brand-electric', bgColor: 'bg-brand-electric/20', title: 'Publish the new version', body: 'Upload the rebranded MP4 to YouTube. Same knowledge, modern delivery. Outperforms the original.' }
    ]
  }
];

const PipelineToggle = () => {
  const [activeTab, setActiveTab] = useState(0);

  return (
    <section className="py-28 relative border-t border-white/5">
      {/* Decorative separator */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-electric/50 to-transparent"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Mobile: Dropdown Style */}
        <div className="md:hidden mb-12">
          <FadeIn>
            <div className="glass-card border border-white/10">
              {pipelineData.map((data, idx) => (
                <button
                  key={data.id}
                  onClick={() => setActiveTab(idx)}
                  className={`w-full text-left px-6 py-4 transition-all border-b border-white/5 last:border-b-0 ${
                    activeTab === idx
                      ? 'bg-gradient-to-r from-brand-neon/20 to-brand-electric/20 text-white'
                      : 'text-white/70 hover:bg-white/5'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-sm">{data.shortTab}</span>
                    {activeTab === idx && (
                      <div className="w-2 h-2 rounded-full bg-brand-electric animate-pulse"></div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </FadeIn>
        </div>

        {/* Desktop: Toggle Buttons */}
        <FadeIn className="hidden md:flex justify-center mb-20">
          <div className="inline-flex glass-card p-1.5 rounded-full border border-white/10 shadow-xl">
            {pipelineData.map((data, idx) => (
              <button
                key={data.id}
                onClick={() => setActiveTab(idx)}
                className={`px-8 py-3.5 rounded-full text-sm font-bold transition-all whitespace-nowrap ${
                  activeTab === idx
                    ? 'bg-gradient-to-r from-brand-neon to-brand-electric text-white shadow-lg shadow-brand-electric/30'
                    : 'text-white/70 hover:text-white hover:bg-white/5'
                }`}
              >
                {data.tab}
              </button>
            ))}
          </div>
        </FadeIn>

        {/* Content */}
        <div className="min-h-[500px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
            >
              <h3 className="text-2xl md:text-5xl font-black text-center mb-12 md:mb-16 text-white px-4">
                {pipelineData[activeTab].heading}
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                {pipelineData[activeTab].steps.map((step, idx) => (
                  <div
                    key={idx}
                    className="glass-card p-6 md:p-8 relative group border border-white/10 hover:border-brand-electric/40 transition-all hover:shadow-xl hover:shadow-brand-electric/10"
                  >
                    {/* Progress line connector (desktop only) */}
                    {idx < 3 && (
                      <div className="hidden md:block absolute top-12 -right-3 w-6 h-0.5 bg-gradient-to-r from-brand-electric/50 to-transparent"></div>
                    )}

                    <div className={`w-14 h-14 rounded-xl ${step.bgColor} border border-white/20 flex items-center justify-center mb-6 shadow-lg`}>
                      <SafeIcon icon={step.icon} className={`text-2xl ${step.iconColor}`} />
                    </div>

                    <div className="text-xs font-black text-brand-electric mb-3 uppercase tracking-widest bg-brand-electric/10 px-3 py-1 rounded-full inline-block">
                      Step {idx + 1}
                    </div>

                    <h4 className="text-lg md:text-xl font-bold text-white mb-4">{step.title}</h4>
                    <p className="text-white/70 text-sm leading-relaxed">{step.body}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

      </div>
    </section>
  );
};

export default PipelineToggle;